/** @jsxImportSource @emotion/react */

import React, { useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import TextBlock from '@component/common/block/text-block';
import Line from '@component/common/line';
import IconBlock from '@component/common/block/icon-block';
import Gap from '@component/common/gap';
import { v4 as uuid4 } from 'uuid';

type TComponentSize = 'small' | 'medium' | 'large';

interface IProps {
  marks: Date[];
  calendarType?: 'attendance' | 'study';
  month?: Date;
  handleMonthNavigationClick?: (month: Date) => void;
  selectedDate?: Date;
  handleDateClick?: (date: Date) => void;
  startDate?: Date;
  theme?: ETheme;
  size?: TComponentSize;
  width?: '100%' | 'fit-content' | string;
  height?: '100%' | 'auto' | string;
}

const createDesign = (componentSize: TComponentSize, theme: ETheme) => {
  // ############### createSize ###############
  const createSize = () => {
    switch (componentSize) {
      case 'small':
        return {
          cellHeight: '20px',
          padding: '4px',
          gap: '3px',
          textSize: 'small' as const,
          navigationIconSize: 'small' as const,
          borderRadius: size.BORDER_RADIUS_1,
        };
      case 'large':
        return {
          cellHeight: '35px',
          padding: '8px',
          gap: '8px',
          textSize: 'large' as const,
          navigationIconSize: 'medium' as const,
          borderRadius: size.BORDER_RADIUS_2,
        };
      case 'medium':
      default:
        return {
          cellHeight: '25px',
          padding: '5px',
          gap: '5px',
          textSize: 'medium' as const,
          navigationIconSize: 'small' as const,
          borderRadius: size.BORDER_RADIUS_1,
        };
    }
  };

  // ############### createTheme ###############
  const createTheme = () => {
    switch (theme) {
      case ETheme.BLACK:
        return {
          backgroundColor: color.WHITE_1,
          textColor: color.BLACK_2,
          borderColor: color.BLACK_2,
          markBackgroundColor: color.BLACK_2,
          studyBackgroundColor: color.GRAY_4,
          markTextColor: color.WHITE_1,
          lineColor: color.GRAY_3,
        };
      case ETheme.GRAY:
        return {
          backgroundColor: color.WHITE_1,
          textColor: color.BLACK_2,
          borderColor: color.GRAY_2,
          markBackgroundColor: color.GRAY_2,
          studyBackgroundColor: color.GRAY_4,
          markTextColor: color.WHITE_1,
          lineColor: color.GRAY_3,
        };
      case ETheme.GREEN:
        return {
          backgroundColor: color.WHITE_1,
          textColor: color.BLACK_2,
          borderColor: color.GREEN_3,
          markBackgroundColor: color.GREEN_3,
          studyBackgroundColor: color.GREEN_5,
          markTextColor: color.WHITE_1,
          lineColor: color.GREEN_5,
        };
      case ETheme.BROWN:
      default:
        return {
          backgroundColor: color.WHITE_1,
          textColor: color.BLACK_2,
          borderColor: color.GRAY_BOX_BORDER_COLOR,
          markBackgroundColor: color.BROWN_1,
          studyBackgroundColor: color.BROWN_4,
          markTextColor: color.WHITE_1,
          lineColor: color.GRAY_3,
        };
    }
  };

  // ############### Combine ###############
  const sizeDesign = createSize();
  const themeDesign = createTheme();

  return {
    // size
    cellHeight: sizeDesign.cellHeight,
    padding: sizeDesign.padding,
    gap: sizeDesign.gap,
    textSize: sizeDesign.textSize,
    navigationIconSize: sizeDesign.navigationIconSize,
    borderRadius: sizeDesign.borderRadius,
    // theme
    backgroundColor: themeDesign.backgroundColor,
    textColor: themeDesign.textColor,
    borderColor: themeDesign.borderColor,
    markBackgroundColor: themeDesign.markBackgroundColor,
    markTextColor: themeDesign.markTextColor,
    studyBackgroundColor: themeDesign.studyBackgroundColor,
    lineColor: themeDesign.lineColor,
  };
};

interface IWrapperProps {
  $design: ReturnType<typeof createDesign>;
  $width: string;
  $height: string;
}

const Wrapper = styled.div<IWrapperProps>`
  display: flex;
  flex-flow: column nowrap;
  width: ${(props) => props.$width};
  height: ${(props) => props.$height};
  border-radius: ${(props) => props.$design.borderRadius};
  border: 1px solid ${(props) => props.$design.borderColor};
  padding: ${(props) => props.$design.padding};
  background-color: ${(props) => props.$design.backgroundColor};
`;

const CalendarGrid = styled.div<{ $gap: string; $cellHeight: string }>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${(props) => props.$gap};
  width: 100%;
  grid-auto-rows: ${(props) => props.$cellHeight};
`;

const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CalendarGridCell = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StudyDot = styled.span<{ $color: string }>`
  position: absolute;
  bottom: 5px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
  pointer-events: none;
`;

// 출석 체크 (초록색 테두리 원)
const Check = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border: 2px solid ${color.GREEN_4};
  border-radius: 50%;
  background-color: transparent;
`;

// 미출석 (빨간색 X)
const Uncheck = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: ${color.RED_3};
  line-height: 1;
`;

const Calendar: React.FC<IProps> = ({
  marks,
  calendarType = 'study',
  startDate,
  month,
  handleMonthNavigationClick,
  selectedDate,
  handleDateClick,
  theme = ETheme.BROWN,
  size: componentSize = 'medium',
  width = 'fit-content',
  height = 'auto',
}) => {
  const design = createDesign(componentSize, theme);
  const cellHeight = calendarType === 'study' ? `calc(${design.cellHeight} + ${size.MARGIN_1})` : design.cellHeight;
  const [currentDate] = useState(new Date());
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const displayDate = month ?? currentDate;
  const currentYear = displayDate.getFullYear();
  const currentMonth = displayDate.getMonth();

  const firstDateOfMonth = new Date(currentYear, currentMonth, 1);
  const beginDateOfCal = new Date(firstDateOfMonth);
  beginDateOfCal.setDate(1 - firstDateOfMonth.getDay());

  const lastDateOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const endDateOfCal = new Date(lastDateOfMonth);
  endDateOfCal.setDate(lastDateOfMonth.getDate() + (6 - lastDateOfMonth.getDay()));

  const groupDatesByWeek = (beginDate: Date, endDate: Date) => {
    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];
    const iterDate = new Date(beginDate);

    while (iterDate <= endDate) {
      currentWeek.push(new Date(iterDate));
      if (currentWeek.length === 7 || iterDate.getDay() === 6) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      iterDate.setDate(iterDate.getDate() + 1);
    }

    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    return weeks;
  };

  const weeks = groupDatesByWeek(beginDateOfCal, endDateOfCal);

  const isMarked = (day: Date) => {
    return marks.some(
      (mark) =>
        mark.getFullYear() === day.getFullYear() &&
        mark.getMonth() === day.getMonth() &&
        mark.getDate() === day.getDate(),
    );
  };

  return (
    <Wrapper $design={design} $width={width} $height={height}>
      {handleMonthNavigationClick && (
        <>
          <MonthNavigation>
            <IconBlock
              icon='left'
              design={{
                icon: { size: design.navigationIconSize, color: design.textColor },
                box: { padding: design.padding, event: { cursor: 'pointer' } },
              }}
              click={{ handler: () => handleMonthNavigationClick(new Date(currentYear, currentMonth - 1, 1)) }}
            />
            <TextBlock
              text={`${currentYear}년 ${currentMonth + 1}월`}
              design={{ text: { size: design.textSize, color: design.textColor, bold: true } }}
            />
            <IconBlock
              icon='right'
              design={{
                icon: { size: design.navigationIconSize, color: design.textColor },
                box: { padding: design.padding, event: { cursor: 'pointer' } },
              }}
              click={{ handler: () => handleMonthNavigationClick(new Date(currentYear, currentMonth + 1, 1)) }}
            />
          </MonthNavigation>
          <Gap height={size.MARGIN_1} />
        </>
      )}
      <CalendarGrid $gap={design.gap} $cellHeight={cellHeight}>
        {['일', '월', '화', '수', '목', '금', '토'].map((day) => (
          <TextBlock
            key={uuid4()}
            text={day}
            design={{
              text: { size: design.textSize, color: design.textColor, bold: true },
            }}
          />
        ))}
      </CalendarGrid>
      <Line color={design.lineColor} margin={`${design.gap} 0px`} />
      <CalendarGrid $gap={design.gap} $cellHeight={cellHeight}>
        {weeks.map((week) =>
          week.map((day) => {
            const isCurrentMonth = day.getMonth() === currentMonth;
            const marked = isMarked(day);
            const dayDate = new Date(day);
            dayDate.setHours(0, 0, 0, 0);
            const isFuture = dayDate > today;
            const isToday = dayDate.getTime() === today.getTime();
            const isSelected =
              !!selectedDate &&
              selectedDate.getFullYear() === day.getFullYear() &&
              selectedDate.getMonth() === day.getMonth() &&
              selectedDate.getDate() === day.getDate();

            // attendance 모드일 때 O/X 표시 여부 (미래 날짜, 가입 이전 날짜는 표시 안함)
            const isBeforeStart = startDate
              ? dayDate < new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
              : false;
            const showAttendanceMark = calendarType === 'attendance' && isCurrentMonth && !isFuture && !isBeforeStart;

            const showStudyMark = calendarType === 'study' && isCurrentMonth && marked;
            const canSelect = isCurrentMonth && !!handleDateClick && (calendarType !== 'study' || !isFuture);

            return (
              <CalendarGridCell key={uuid4()}>
                <TextBlock
                  text={day.getDate().toString()}
                  click={canSelect ? { handler: () => handleDateClick?.(day) } : undefined}
                  design={{
                    text: {
                      size: design.textSize,
                      color: !isCurrentMonth
                        ? 'transparent'
                        : calendarType === 'study' && isSelected
                          ? design.markTextColor
                          : calendarType === 'study' && isFuture
                            ? color.GRAY_2
                            : design.textColor,
                      bold: calendarType === 'study' && isToday,
                    },
                    box: {
                      alignX: 'center',
                      alignY: 'center',
                      width: '100%',
                      height: '100%',
                      borderWidth: isSelected ? size.BORDER_WIDTH_1 : undefined,
                      borderColor: isSelected ? design.markBackgroundColor : undefined,
                      borderRadius: design.borderRadius,
                      padding: calendarType === 'study' ? '0 0 8px' : undefined,
                      backgroundColor:
                        calendarType === 'study' && isCurrentMonth
                          ? isSelected
                            ? design.markBackgroundColor
                            : showStudyMark
                              ? design.studyBackgroundColor
                              : color.GRAY_5
                          : undefined,
                      event: { cursor: canSelect ? 'pointer' : 'auto' },
                    },
                  }}
                />
                {calendarType === 'study' && isCurrentMonth && (
                  <StudyDot
                    $color={
                      showStudyMark ? (isSelected ? design.markTextColor : design.markBackgroundColor) : 'transparent'
                    }
                  />
                )}
                {showAttendanceMark && (marked ? <Check /> : !isToday && <Uncheck>×</Uncheck>)}
              </CalendarGridCell>
            );
          }),
        )}
      </CalendarGrid>
    </Wrapper>
  );
};

export default Calendar;
